import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { AvisData, fmtDateFr, lotTechniqueLabel, lotGeoLabel, panierLabel } from '../../composants/AvisPrestataire/avisTypes';

// Avis regroupes par contrepartie (prestataire cote acheteur, acheteur cote prestataire),
// comme l'accordeon de l'onglet Evaluations.
interface Group {
  name: string;
  average: number;
  items: AvisData[];
}

const buildGroups = (avis: AvisData[], cote: 'acheteur' | 'prestataire'): Group[] => {
  // Cote acheteur : on regroupe par prestataire (entite). Cote prestataire : par acheteur (organisme).
  const byPrestataire = cote === 'acheteur';
  const idOf = (a: AvisData) => (byPrestataire ? a.entite_id : a.organisme_id);
  const nameOf = (a: AvisData) =>
    (byPrestataire ? a.entite_nom : a.organisme_nom) ?? (byPrestataire ? 'Prestataire' : 'Acheteur');

  const map = new Map<number, AvisData[]>();
  avis.forEach((a) => {
    const key = idOf(a);
    const items = map.get(key) ?? [];
    items.push(a);
    map.set(key, items);
  });

  return Array.from(map.values())
    .map((items) => ({
      name: nameOf(items[0]),
      average: Math.round((items.reduce((sum, a) => sum + a.note, 0) / items.length) * 10) / 10,
      items: items.slice().sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? '')),
    }))
    .sort((a, b) => b.items.length - a.items.length);
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingHorizontal: 28,
    paddingBottom: 46, // laisse la place au pied de page fixe
    fontSize: 11,
    fontFamily: 'Helvetica',
    backgroundColor: '#FFFFFF',
    color: '#374151',
  },
  title: { fontSize: 18, fontFamily: 'Helvetica-Bold', color: '#595959' },
  subtitle: { fontSize: 12, color: '#6B7280', marginTop: 4 },
  globalNote: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: '#f97316', marginTop: 12, marginBottom: 6 },
  empty: { fontSize: 12, color: '#6B7280', marginTop: 16 },
  group: { marginTop: 14 },
  groupHead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 4,
  },
  groupName: { fontSize: 13, fontFamily: 'Helvetica-Bold', color: '#374151' },
  groupNote: { fontSize: 11, color: '#6B7280' },
  row: {
    marginTop: 8,
    padding: 10,
    backgroundColor: '#F9FAFB',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 4,
  },
  rowId: { fontSize: 10.5, marginBottom: 2 },
  rowKey: { color: '#9CA3AF', fontFamily: 'Helvetica-Bold' },
  rowNote: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: '#374151', marginTop: 4 },
  rowDate: { fontFamily: 'Helvetica', color: '#9CA3AF' },
  rowComment: { fontSize: 10.5, fontStyle: 'italic', color: '#374151', marginTop: 4, lineHeight: 1.4 },
  footer: {
    position: 'absolute',
    bottom: 18,
    left: 28,
    right: 28,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 6,
  },
  footerText: { fontSize: 9, color: '#9CA3AF' },
});

interface AvisPDFProps {
  avis: AvisData[];
  cote: 'acheteur' | 'prestataire';
  // Sens de la collection : 'donnes' = avis emis par le lecteur, 'recus' = avis recus par le lecteur.
  // Omis -> sens public historique (acheteur depose, prestataire recoit). Le miroir PRIVE inverse :
  // acheteur -> 'recus' (des prestataires), prestataire -> 'donnes' (aux acheteurs).
  sens?: 'donnes' | 'recus';
  // Date + heure exactes de generation (ex. "17/06/2026 14:32:05"), fournies par l'appelant.
  generatedAt: string;
  // Auteur de la generation (utilisateur connecte).
  generatedBy: string;
}

/**
 * Export PDF de tous les avis d'une page Evaluations :
 * - cote acheteur : les avis qu'il a deposes sur les prestataires ("Avis concernant les prestataires") ;
 * - cote prestataire : les avis recus par son entite.
 * Pied de page fixe (sur chaque page) : auteur + date/heure de generation + pagination.
 */
const AvisPDF: React.FC<AvisPDFProps> = ({ avis, cote, sens, generatedAt, generatedBy }) => {
  const groups = buildGroups(avis, cote);
  const count = avis.length;
  const globalAverage = count ? Math.round((avis.reduce((sum, a) => sum + a.note, 0) / count) * 100) / 100 : null;

  // Sens effectif : par defaut le sens public historique (acheteur depose, prestataire recoit).
  const sensEff = sens ?? (cote === 'acheteur' ? 'donnes' : 'recus');
  const subject = cote === 'acheteur' ? avis[0]?.organisme_nom : avis[0]?.entite_nom;
  const title = cote === 'acheteur'
    ? (sensEff === 'donnes' ? 'Avis concernant les prestataires' : 'Avis des prestataires concernant votre structure')
    : (sensEff === 'donnes' ? 'Avis donnés aux acheteurs' : 'Avis reçus');
  const subtitle = sensEff === 'donnes'
    ? `Avis émis${subject ? ` par ${subject}` : ''}`
    : `Avis reçus${subject ? ` par ${subject}` : ''}`;

  return (
    <Document>
      <Page style={styles.page} wrap>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.subtitle}>{subtitle}</Text>

        {count === 0 ? (
          <Text style={styles.empty}>Aucun avis pour le moment.</Text>
        ) : (
          <>
            <Text style={styles.globalNote}>
              Note globale {globalAverage != null ? globalAverage.toFixed(2) : '—'}/5 ({count} avis)
            </Text>
            {groups.map((group, groupIndex) => (
              <View key={groupIndex} style={styles.group}>
                <View style={styles.groupHead}>
                  <Text style={styles.groupName}>{group.name}</Text>
                  <Text style={styles.groupNote}>{group.average}/5 · {group.items.length} avis</Text>
                </View>
                {group.items.map((a, rowIndex) => (
                  <View key={rowIndex} style={styles.row} wrap={false}>
                    <Text style={styles.rowId}><Text style={styles.rowKey}>Lot technique : </Text>{lotTechniqueLabel(a)}</Text>
                    <Text style={styles.rowId}><Text style={styles.rowKey}>Lot géographique : </Text>{lotGeoLabel(a)}</Text>
                    <Text style={styles.rowId}><Text style={styles.rowKey}>Panier : </Text>{panierLabel(a)}</Text>
                    <Text style={styles.rowNote}>
                      {a.note}/5
                      {a.updated_at ? <Text style={styles.rowDate}> · Avis du {fmtDateFr(a.updated_at)}</Text> : null}
                    </Text>
                    {a.commentaire ? <Text style={styles.rowComment}>« {a.commentaire} »</Text> : null}
                  </View>
                ))}
              </View>
            ))}
          </>
        )}

        {/* Pied de page fixe : repete sur chaque page (auteur + date/heure + pagination). */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>Document généré le {generatedAt} par {generatedBy}</Text>
          <Text style={styles.footerText} render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};

export default AvisPDF;
